import axios from 'axios';
import {API_URL, MEDIA_TYPE_V2} from '../config/constants';
import {
    Component,
    ComponentLifecycleTransition,
    ComponentState,
    ComponentSummary,
    ComponentVersion,
    ReleaseLevel,
} from '../@types/component';
import {makeComponent, makeComponentSummary} from '../utils/component-utils';
import {getErrorMessage} from '../utils/utils';
import {ModelLintRequest, ModelLintSnapshot} from '../@types/model-quality';
import {TestMappingRequest, TestMappingSnapshot} from '../@types/test-mapping';
import {ShakedownRequest, ShakedownSnapshot} from '../@types/model-quality';
import SpringDataPage, {BasicPage} from '../@types/spring-data-page';

export interface ComponentPageFilters {
    searchTerm?: string;
    groupId?: number | string | null;
    includeSubGroups?: boolean;
    states?: ComponentState[];
    // Only Components with a completed mapping run against this exact report source.
    mappedAgainstSource?: string;
    // Only Components missing a completed mapping run against at least one report source the
    // caller can reach (component-solution-spec.md section 5).
    hasUnmappedSources?: boolean;
}

class ComponentService {
    static componentUrl = API_URL + 'api/v2/components';

    private formComponentsPage(data: any): SpringDataPage<ComponentSummary> {
        const componentsPage = {} as SpringDataPage<ComponentSummary>;
        const list = data?._embedded?.componentSummaryDTOList;
        componentsPage.content = Array.isArray(list) ? list.map((c: any) => makeComponentSummary(c)) : [];
        componentsPage.page = data?.page;
        return componentsPage;
    }

    /**
     * Paged, searchable list of Component summaries -- the Component analogue of
     * SafetyService.fetchPagedSafetyModels.
     */
    fetchPagedComponents(page: BasicPage, filters: ComponentPageFilters = {}): Promise<SpringDataPage<ComponentSummary>> {
        const params = new URLSearchParams();
        params.set('size', String(page.size));
        params.set('page', String(page.offset));
        params.set('sort', `${page.sort},${page.direction}`);
        if (filters.searchTerm && filters.searchTerm !== '') {
            params.set('searchTerm', filters.searchTerm);
        }
        if (filters.groupId) {
            params.set('groupId', String(filters.groupId));
            if (filters.includeSubGroups) {
                params.set('includeSubGroups', 'true');
            }
        }
        (filters.states ?? []).forEach(state => params.append('states', state));
        if (filters.mappedAgainstSource) {
            params.set('mappedAgainstSource', filters.mappedAgainstSource);
        }
        if (filters.hasUnmappedSources) {
            params.set('hasUnmappedSources', 'true');
        }

        return axios
            .get(`${ComponentService.componentUrl}/paged?${params.toString()}`, {
                withCredentials: true,
                headers: {'Content-Type': MEDIA_TYPE_V2},
            })
            .then(response => this.formComponentsPage(response.data))
            .catch(err => {
                throw new Error('Failed to get paged components: ' + getErrorMessage(err));
            });
    }

    /**
     * Creates a DRAFT Component in the given group (POST /api/v2/components/{groupId}). The caller
     * supplies the {@code uniqueId}; it is mandatory. Returns the new Component with its
     * freshly-created fault tree.
     */
    createComponent(
        groupId: number | string,
        data: { uniqueId: string; name: string; description?: string },
    ): Promise<Component> {
        return axios
            .post(`${ComponentService.componentUrl}/${groupId}`, {
                uniqueId: data.uniqueId,
                name: data.name,
                description: data.description ?? '',
            }, {withCredentials: true, headers: {'Content-Type': 'application/json'}})
            .then(response => makeComponent(response.data))
            .catch(err => {
                throw new Error('Failed to create component: ' + getErrorMessage(err));
            });
    }

    /**
     * Fetches a single Component, including its interior fault tree -- enough to load the Component
     * into the workspace.
     */
    getComponent(componentId: number | string): Promise<Component> {
        return axios
            .get(`${ComponentService.componentUrl}/${componentId}`, {withCredentials: true})
            .then(response => makeComponent(response.data))
            .catch(err => {
                throw new Error(`Failed to get component ${componentId}: ` + getErrorMessage(err));
            });
    }

    /**
     * Updates a DRAFT Component's unique identifier, name and description
     * (PATCH /api/v2/components/{id}). The group is immutable. Returns the Component with its
     * fault tree.
     */
    updateComponent(
        componentId: number | string,
        changes: { uniqueId: string; name: string; description?: string },
    ): Promise<Component> {
        return axios
            .patch(`${ComponentService.componentUrl}/${componentId}`, {
                uniqueId: changes.uniqueId,
                name: changes.name,
                description: changes.description ?? '',
            }, {withCredentials: true, headers: {'Content-Type': 'application/json'}})
            .then(response => makeComponent(response.data))
            .catch(err => {
                throw new Error(`Failed to update component ${componentId}: ` + getErrorMessage(err));
            });
    }

    /**
     * Deletes a DRAFT Component (DELETE /api/v2/components/{id}). The server rejects the request
     * (400) if the Component is not in the DRAFT state.
     */
    deleteComponent(componentId: number | string): Promise<void> {
        return axios
            .delete(`${ComponentService.componentUrl}/${componentId}`, {withCredentials: true})
            .then(() => undefined)
            .catch(err => {
                throw new Error(`Failed to delete component ${componentId}: ` + getErrorMessage(err));
            });
    }

    /**
     * Fetches every version of a Component, by its uniqueId.
     */
    /**
     * Applies a lifecycle transition: publish a draft, deprecate a published version, or archive
     * one. None of them can be undone, so callers confirm before asking.
     *
     * {@code releaseLevel} names how big a release this is (MAJOR, MINOR or PATCH), which fixes
     * the new version number; required when publishing, ignored otherwise. {@code releaseNotes}
     * says what changed since the previous release; also required when publishing, ignored
     * otherwise.
     */
    applyLifecycleTransition(
        componentId: number | string,
        transition: ComponentLifecycleTransition,
        releaseLevel?: ReleaseLevel,
        releaseNotes?: string,
    ): Promise<Component> {
        const params = releaseLevel ? `?releaseLevel=${releaseLevel}` : '';
        const body = releaseNotes ? {releaseNotes} : null;
        return axios
            .post(`${ComponentService.componentUrl}/${componentId}/lifecycle/${transition}${params}`, body,
                {withCredentials: true})
            .then(response => response.data as Component)
            .catch(err => {
                throw new Error(`Failed to ${transition.toLowerCase()} the component: ` + getErrorMessage(err));
            });
    }

    getComponentVersions(uniqueId: string): Promise<Component[]> {
        return axios
            .get(`${ComponentService.componentUrl}/versions/${encodeURIComponent(uniqueId)}`, {withCredentials: true})
            .then(response => {
                const list = response.data?._embedded?.componentDTOList;
                return Array.isArray(list) ? list.map((c: any) => makeComponent(c)) : [];
            })
            .catch(err => {
                throw new Error(`Failed to get versions for component ${uniqueId}: ` + getErrorMessage(err));
            });
    }

    /**
     * Adds a node to the DRAFT Component's fault tree (POST /api/v2/components/{id}/faultTreeNodes).
     * {@code nodeDto} carries the parent id and any child ids to adopt. Returns the Component with
     * its refreshed fault tree.
     */
    addFaultTreeNode(componentId: number | string, nodeDto: any): Promise<Component> {
        return axios
            .post(`${ComponentService.componentUrl}/${componentId}/faultTreeNodes`, nodeDto,
                {withCredentials: true, headers: {'Content-Type': 'application/json'}})
            .then(response => makeComponent(response.data))
            .catch(err => {
                throw new Error('Failed to add fault tree node: ' + getErrorMessage(err));
            });
    }

    /**
     * Updates a node in the DRAFT Component's fault tree, properties and/or parent
     * (PATCH /api/v2/components/{id}/faultTreeNodes). {@code nodeDto.id} identifies the target.
     */
    updateFaultTreeNode(componentId: number | string, nodeDto: any): Promise<Component> {
        return axios
            .patch(`${ComponentService.componentUrl}/${componentId}/faultTreeNodes`, nodeDto,
                {withCredentials: true, headers: {'Content-Type': 'application/json'}})
            .then(response => makeComponent(response.data))
            .catch(err => {
                throw new Error('Failed to update fault tree node: ' + getErrorMessage(err));
            });
    }

    /**
     * Deletes a node from the DRAFT Component's fault tree
     * (DELETE /api/v2/components/{id}/faultTreeNodes/{nodeId}). {@code preserveChildIds} are
     * re-parented onto the deleted node's parent instead of being removed.
     */
    deleteFaultTreeNode(componentId: number | string, nodeId: number, preserveChildIds: number[] = []): Promise<Component> {
        const params = new URLSearchParams();
        preserveChildIds.forEach(id => params.append('childrenToPreserve', String(id)));
        const query = params.toString() ? `?${params.toString()}` : '';
        return axios
            .delete(`${ComponentService.componentUrl}/${componentId}/faultTreeNodes/${nodeId}${query}`,
                {withCredentials: true})
            .then(response => makeComponent(response.data))
            .catch(err => {
                throw new Error('Failed to delete fault tree node: ' + getErrorMessage(err));
            });
    }

    /**
     * Reorders a node's children in the DRAFT Component's fault tree
     * (PATCH /api/v2/components/{id}/faultTreeNodes/{parentNodeId}/reorder).
     */
    reorderFaultTreeNodeChildren(componentId: number | string, parentNodeId: number, childIds: number[]): Promise<Component> {
        return axios
            .patch(`${ComponentService.componentUrl}/${componentId}/faultTreeNodes/${parentNodeId}/reorder`,
                childIds, {withCredentials: true, headers: {'Content-Type': 'application/json'}})
            .then(response => makeComponent(response.data))
            .catch(err => {
                throw new Error('Failed to reorder fault tree node children: ' + getErrorMessage(err));
            });
    }

    /**
     * Deep-copies the subtree rooted at {@code sourceNodeId} (from any fault tree the caller can
     * READ -- another Component or an event sequence) into this Component's fault tree, as a new
     * child of {@code destinationNodeId}. Returns the Component with its refreshed fault tree.
     */
    copyPasteFaultTreeNode(componentId: number | string, sourceNodeId: number, destinationNodeId: number): Promise<Component> {
        const url = `${ComponentService.componentUrl}/${componentId}/faultTreeNodes`
            + `/copyAndPaste/sourceId/${sourceNodeId}/destinationId/${destinationNodeId}`;
        return axios
            .post(url, {}, {withCredentials: true, headers: {'Content-Type': 'application/json'}})
            .then(response => makeComponent(response.data))
            .catch(err => {
                throw new Error('Failed to copy fault tree node into component: ' + getErrorMessage(err));
            });
    }

    /**
     * Starts a model lint run over a Component's fault tree. The same run a Safety View gets: the
     * checks are about the tree, not about what owns it.
     */
    startModelLint(componentId: number, request: ModelLintRequest): Promise<{ runId: string }> {
        return axios
            .post(`${ComponentService.componentUrl}/${componentId}/lint`, request, {
                withCredentials: true,
                headers: {'Content-Type': 'application/json'},
            })
            .then(response => response.data as { runId: string })
            .catch(err => {
                throw new Error('Failed to start model lint: ' + getErrorMessage(err));
            });
    }

    /**
     * Maps one report against a Component's fault tree, the same run a Safety View gets. Returns
     * immediately; the decision log and result are polled from the snapshot.
     */
    startTestMapping(componentId: number, request: TestMappingRequest): Promise<{ runId: string }> {
        return axios
            .post(`${ComponentService.componentUrl}/${componentId}/test-mapping`, request, {
                withCredentials: true,
                headers: {'Content-Type': 'application/json'},
            })
            .then(response => response.data as { runId: string })
            .catch(err => {
                throw new Error('Failed to start test mapping: ' + getErrorMessage(err));
            });
    }

    /** Polls a component test mapping run; the log accumulates until the snapshot reports done. */
    getTestMappingSnapshot(componentId: number, runId: string): Promise<TestMappingSnapshot> {
        return axios
            .get(`${ComponentService.componentUrl}/${componentId}/test-mapping/${runId}`, {
                withCredentials: true,
                headers: {'Content-Type': 'application/json'},
            })
            .then(response => response.data as TestMappingSnapshot)
            .catch(err => {
                throw new Error('Failed to fetch test mapping status: ' + getErrorMessage(err));
            });
    }

    /**
     * Starts a shakedown over many reports against a Component's fault tree: the same ephemeral
     * quality assessment a Safety View gets. Persists nothing; the Component's full mapping run is
     * the separate thing that writes evidence.
     */
    startShakedown(componentId: number, request: ShakedownRequest): Promise<{ runId: string }> {
        return axios
            .post(`${ComponentService.componentUrl}/${componentId}/shakedown`, request, {
                withCredentials: true,
                headers: {'Content-Type': 'application/json'},
            })
            .then(response => response.data as { runId: string })
            .catch(err => {
                throw new Error('Failed to start shakedown: ' + getErrorMessage(err));
            });
    }

    /** Polls a component shakedown run; rows accumulate until the snapshot reports done. */
    getShakedownSnapshot(componentId: number, runId: string): Promise<ShakedownSnapshot> {
        return axios
            .get(`${ComponentService.componentUrl}/${componentId}/shakedown/${runId}`, {
                withCredentials: true,
                headers: {'Content-Type': 'application/json'},
            })
            .then(response => response.data as ShakedownSnapshot)
            .catch(err => {
                throw new Error('Failed to fetch shakedown status: ' + getErrorMessage(err));
            });
    }

    /** Polls a component lint run; findings accumulate until the snapshot reports done. */
    getModelLintSnapshot(componentId: number, runId: string): Promise<ModelLintSnapshot> {
        return axios
            .get(`${ComponentService.componentUrl}/${componentId}/lint/${runId}`, {
                withCredentials: true,
                headers: {'Content-Type': 'application/json'},
            })
            .then(response => response.data as ModelLintSnapshot)
            .catch(err => {
                throw new Error('Failed to fetch model lint status: ' + getErrorMessage(err));
            });
    }

    /**
     * A DRAFT Component's edit history, newest first (GET /api/v2/components/{id}/history). With
     * snapshots, each entry carries the Component as it was then.
     */
    fetchHistory(
        componentId: number | string,
        page: number,
        size: number,
        withSnapshots: boolean = false,
    ): Promise<SpringDataPage<ComponentVersion>> {
        return axios
            .get(`${ComponentService.componentUrl}/${componentId}/history?page=${page}&size=${size}&sort=id,DESC`
                + `&snapshots=${withSnapshots}`, {withCredentials: true})
            .then(response => ({
                content: (response.data?.content ?? []).map((version: ComponentVersion): ComponentVersion => (
                    version.snapshot ? {...version, snapshot: makeComponent(version.snapshot)} : version)),
                page: response.data?.page,
            }) as SpringDataPage<ComponentVersion>)
            .catch(err => {
                throw new Error('Failed to load the component history: ' + getErrorMessage(err));
            });
    }

    /** The Component as it was at one history entry, fault tree included. */
    getHistoryVersion(componentId: number | string, versionId: number): Promise<Component> {
        return axios
            .get(`${ComponentService.componentUrl}/${componentId}/history/${versionId}`, {withCredentials: true})
            .then(response => makeComponent(response.data))
            .catch(err => {
                throw new Error('Failed to load the component version: ' + getErrorMessage(err));
            });
    }

    /** Undoes the most recent change to a DRAFT Component; unchanged when there is nothing to undo. */
    undo(componentId: number | string): Promise<Component> {
        return axios
            .post(`${ComponentService.componentUrl}/${componentId}/undo`, null, {withCredentials: true})
            .then(response => makeComponent(response.data))
            .catch(err => {
                throw new Error('Failed to undo changes: ' + getErrorMessage(err));
            });
    }

    /** Redoes the most recently undone change; unchanged when there is nothing to redo. */
    redo(componentId: number | string): Promise<Component> {
        return axios
            .post(`${ComponentService.componentUrl}/${componentId}/redo`, null, {withCredentials: true})
            .then(response => makeComponent(response.data))
            .catch(err => {
                throw new Error('Failed to redo changes: ' + getErrorMessage(err));
            });
    }

    /** Restores a DRAFT Component to a history entry, recorded as a new entry. */
    restoreHistoryVersion(componentId: number | string, versionId: number, comment?: string): Promise<Component> {
        const params = comment ? `?comment=${encodeURIComponent(comment)}` : '';
        return axios
            .post(`${ComponentService.componentUrl}/${componentId}/history/${versionId}/restore${params}`, null,
                {withCredentials: true})
            .then(response => makeComponent(response.data))
            .catch(err => {
                throw new Error('Failed to restore the component version: ' + getErrorMessage(err));
            });
    }

    /** Sets the comment on a history entry; an empty comment clears it. */
    tagHistoryVersion(componentId: number | string, versionId: number, comment: string): Promise<ComponentVersion> {
        return axios
            .patch(`${ComponentService.componentUrl}/${componentId}/history/${versionId}?comment=${encodeURIComponent(comment)}`,
                null, {withCredentials: true})
            .then(response => response.data as ComponentVersion)
            .catch(err => {
                throw new Error('Failed to update the comment: ' + getErrorMessage(err));
            });
    }
}

export default new ComponentService();
